<script lang="ts">
  import gsap from "gsap";
  import { onMount, tick } from "svelte";
  import TiltedGalleryItem from "./TiltedGalleryItem.svelte";
  import Pagination from "./Pagination.svelte";
  import type { TiltedGalleryStoryblok } from "$types/components";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";
  import { ButtonColor } from "$types/atoms";
  import GalleryImage from "$lib/molecules/galleryItems/GalleryImage.svelte";
  import { videoIsPlaying } from "$stores";

  export let blok: TiltedGalleryStoryblok[];

  if (!Array.isArray(blok)) {
    blok = [blok];
  }

  export let backgroundColor = "";
  export let activeBackgroundColor: string;
  export let paginationCounter = false;
  export let mode: "normal" | "fullscreen" | "testimonials-with-logo" =
    "normal";
  export let useDefaultButtonColor = false;
  export let cardsShown = 3;
  export let disableButtonHoverAnimation = false;

  let index = 0;
  const tiltedGalleryItems: Array<TiltedGalleryItem> = [];
  const captions: HTMLDivElement[] = [];
  let buttonColor: ButtonColor = ButtonColor.DARK_LIGHT;
  const aspectRatio = blok[0].aspectRatio;

  onMount(() => {
    if (Object.keys(captions).length) {
      setCaption();
    }

    if (useDefaultButtonColor !== true) {
      if (backgroundColor === "green") {
        buttonColor = ButtonColor.WHITE_LIGHT;
      } else if (backgroundColor === "yellow") {
        buttonColor = ButtonColor.DARK_LIGHT;
      } else if (backgroundColor === "blue") {
        buttonColor = ButtonColor.WHITE_LIGHT;
      } else if (backgroundColor === "green-newsroom") {
        buttonColor = ButtonColor.WHITE;
      }
    }
  });

  function setCaption() {
    for (const key in captions) {
      captions[key].style.display = "none";
    }

    if (captions && captions[index]) {
      captions[index].style.display = "block";
    }
  }

  function onPaginationChange({ detail }: CustomEvent) {
    changeSlide(detail.direction);
  }

  async function changeSlide(direction: -1 | 1) {
    await tick();
    $videoIsPlaying = "force-pause";
    if (index === tiltedGalleryItems.length - 1 && direction === 1)
      return false;
    if (index === 0 && direction === -1) return false;
    index = gsap.utils.clamp(0, blok[0].gallery.length, index + direction);
    tiltedGalleryItems.forEach((item) => {
      item.advance(direction);
    });

    setCaption();
  }
</script>

<div
  class="tilted-gallery {mode} {aspectRatio
    ? 'aspect-ratio-' + aspectRatio
    : ''} {$$props.class}"
  class:has-only-two={blok[0].gallery.length <= 2 || mode === "fullscreen"}
>
  <div class="gallery-container max-w-full">
    {#each blok[0].gallery as item, i}
      {#if item.component === "galleryImage"}
        <TiltedGalleryItem
          index={i}
          blok={item}
          bind:this={tiltedGalleryItems[i]}
          {backgroundColor}
          {cardsShown}
          {mode}
          activeBackgroundColor=""
        >
          <GalleryImage
            blok={item}
            isFullScreen={mode === "fullscreen"}
            slot="slide"
          />
        </TiltedGalleryItem>
      {:else}
        <TiltedGalleryItem
          index={i}
          blok={item}
          bind:this={tiltedGalleryItems[i]}
          {backgroundColor}
          {cardsShown}
          {mode}
          {activeBackgroundColor}
        >
          <StoryblokComponent blok={item} slot="slide" />
        </TiltedGalleryItem>
      {/if}
    {/each}
  </div>
  <div class="bottom">
    {#if blok[0].enableCaptions}
      <div class="captions">
        {#each blok[0].gallery as item, i}
          <div class="type:label-fat" bind:this={captions[i]}>
            {#if item.caption[0]}
              <span class="primary">{item.caption[0].primary}</span>
              <span class="secondary">{item.caption[0].secondary}</span>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
    {#if tiltedGalleryItems.length > 1}
      <div class="pagination-container pb-[16px] md:pb-0">
        <Pagination
          {buttonColor}
          {index}
          total={tiltedGalleryItems.length}
          counterVisible={paginationCounter}
          {disableButtonHoverAnimation}
          on:change={onPaginationChange}
        />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .tilted-gallery {
    margin: 0 auto;
    position: relative;
    perspective: 1000px;
    display: flex;
    flex-direction: column;
    //padding: 0 20px;

    .gallery-container {
      height: fluid(400px, 350px);
    }

    &.hero-newsroom {
      .gallery-container {
        height: auto;
        min-height: 420px;

        @media screen and (min-width: 380px) {
          aspect-ratio: 340/420;
          min-height: 0;
          max-height: 500px;
        }

        @media screen and (min-width: 768px) {
          aspect-ratio: none;
          height: fluid(400px, 350px);
          max-height: auto;
        }
      }

      :global(.tilted-gallery-item:nth-child(2) .mask) {
        position: relative;
        bottom: 2.5%;
        transition: bottom 0.2s;
      }
      :global(.tilted-gallery-item:nth-child(3) .mask) {
        position: relative;
        bottom: 5%;
        transition: bottom 0.2s;
      }
      .bottom {
        margin-top: 80px;

        @media screen and (min-width: 768px) {
          margin-top: 0;
        }
      }
    }

    &.testimonials-with-logo {
      .gallery-container {
        height: fluid(372px, 372px);
        @include breakpoint("small") {
          height: fluid(400px, 316px);
        }
      }
    }

    &.fullscreen {
      .gallery-container {
        aspect-ratio: 355 / 419;
        width: 100%;
        height: 100%;

        @include breakpoint("small") {
          aspect-ratio: 1200 / 674;
        }
      }

      .bottom {
        flex-direction: column;

        @include breakpoint("small") {
          flex-direction: row;
        }
      }

      .pagination-container {
        justify-content: center;

        @include breakpoint("small") {
          justify-content: flex-end;
        }
      }
    }

    // Aspect ratios
    &.aspect-ratio-16-9 {
      .gallery-container {
        aspect-ratio: 16/9;
        height: auto;
      }
    }

    &.aspect-ratio-1200-674 {
      .gallery-container {
        aspect-ratio: 1200/674;
        height: auto;
      }
    }

    &.aspect-ratio-588-400 {
      .gallery-container {
        aspect-ratio: 588/400;
        height: auto;
      }
    }
  }

  .gallery-container {
    transform-style: preserve-3d;
    transition: transform 1s;
  }

  .bottom {
    display: flex;
    margin-top: 80px;
    width: 100%;

    @include breakpoint("small") {
      //margin-top: fluid(88px);
      position: absolute;
      left: 0;
      bottom: calc(#{fluid(120px, 60px)} * -1);
      margin-top: 0;
    }
  }

  .has-only-two {
    .bottom {
      margin-top: 50px;

      @include breakpoint("small") {
        margin-top: 0;
      }
    }
  }

  .fullscreen {
    .bottom {
      margin-top: 60px;

      @include breakpoint("small") {
        margin-top: 0;
      }
    }
  }

  .captions {
    width: 100%;
    text-align: center;
    margin: fluid(16px, 16px) 0 fluid(24px, 24px);

    @include breakpoint("small") {
      text-align: left;
      margin: 0;
      display: flex;
      align-items: center;
    }

    .secondary {
      font-weight: 400;
    }
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }
</style>
